import type { TKey } from '@/lib/i18n'

/**
 * Every shortcut the client answers to, and the only place one is declared.
 *
 * **Keys per platform, written out.** Drive's convention is followed where it
 * has one — its sequences open with `Alt` on Windows and `Control` on a Mac,
 * because `⌥` + a letter types a character there (`⌥C` is «ç») — and a table
 * that spells both columns out is easier to check against Drive's own than a
 * token that means one thing here and another there.
 *
 * A spec is steps separated by a space, each step modifiers and a key joined
 * by `+`: `alt+c f` is `Alt+C`, then `F`. The key is `event.key` lowercased.
 *
 * **An entry nobody registers is only listed.** `⌘B` lives in `@repo/ui`'s
 * sidebar and `Enter`/`Space` on the row itself; they are here so `?` shows
 * them, and the dispatcher never runs an id without a handler.
 *
 * `inFields`: the chord also works from a text field (only chords with `⌘`,
 * `Ctrl` or `Alt` — a bare `/` there is somebody typing a slash).
 * `onlyUnfocused`: the keys reach the registry only when nothing has focus;
 * the arrows on a focused item are the listing's own `onKeyDown`.
 * `unlessTextSelected`: the keys stay the browser's while text is selected on
 * the page, so `⌘C` over a highlighted name copies the name.
 */
export const SHORTCUTS = {
	'search.focus': {
		group: 'general',
		inFields: true,
		label: 'client:shortcuts.action.search_focus',
		mac: ['meta+k', '/'],
		other: ['ctrl+k', '/']
	},
	'shortcuts.help': {
		group: 'general',
		label: 'client:shortcuts.action.help',
		mac: ['?', 'meta+/'],
		other: ['?', 'ctrl+/']
	},
	'sidebar.toggle': {
		group: 'general',
		label: 'client:shortcuts.action.sidebar_toggle',
		mac: ['meta+b'],
		other: ['ctrl+b']
	},
	'listing.move': {
		group: 'listing',
		label: 'client:shortcuts.action.listing_move',
		mac: ['arrowdown', 'arrowup', 'arrowleft', 'arrowright'],
		onlyUnfocused: true,
		other: ['arrowdown', 'arrowup', 'arrowleft', 'arrowright']
	},
	'listing.extend': {
		group: 'listing',
		label: 'client:shortcuts.action.listing_extend',
		mac: ['shift+arrowdown', 'shift+arrowup'],
		other: ['shift+arrowdown', 'shift+arrowup']
	},
	'listing.open': {
		group: 'listing',
		label: 'client:shortcuts.action.listing_open',
		mac: ['enter'],
		other: ['enter']
	},
	'listing.toggle': {
		group: 'listing',
		label: 'client:shortcuts.action.listing_toggle',
		mac: ['space'],
		other: ['space']
	},
	'listing.paste': {
		group: 'listing',
		label: 'client:shortcuts.action.listing_paste',
		mac: ['meta+v'],
		other: ['ctrl+v'],
		unlessTextSelected: true
	},
	'folder.up': {
		group: 'listing',
		label: 'client:shortcuts.action.folder_up',
		mac: ['meta+arrowup'],
		other: ['alt+arrowup']
	},
	'selection.all': {
		group: 'selection',
		label: 'client:shortcuts.action.selection_all',
		mac: ['meta+a'],
		other: ['ctrl+a']
	},
	'selection.clear': {
		group: 'selection',
		label: 'client:shortcuts.action.selection_clear',
		mac: ['escape'],
		other: ['escape']
	},
	'selection.copy': {
		group: 'selection',
		label: 'client:shortcuts.action.selection_copy',
		mac: ['meta+c'],
		other: ['ctrl+c'],
		unlessTextSelected: true
	},
	'selection.rename': {
		group: 'selection',
		label: 'client:shortcuts.action.selection_rename',
		mac: ['f2'],
		other: ['f2']
	},
	'selection.download': {
		group: 'selection',
		label: 'client:shortcuts.action.selection_download',
		mac: ['meta+s'],
		other: ['ctrl+s']
	},
	'selection.delete': {
		group: 'selection',
		label: 'client:shortcuts.action.selection_delete',
		mac: ['delete', 'backspace'],
		other: ['delete', 'backspace']
	},
	'create.folder': {
		group: 'create',
		label: 'client:shortcuts.action.create_folder',
		mac: ['ctrl+c f'],
		other: ['alt+c f']
	},
	'create.upload': {
		group: 'create',
		label: 'client:shortcuts.action.create_upload',
		mac: ['ctrl+c u'],
		other: ['alt+c u']
	},
	'create.upload_folder': {
		group: 'create',
		label: 'client:shortcuts.action.create_upload_folder',
		mac: ['ctrl+c i'],
		other: ['alt+c i']
	}
} as const satisfies Record<
	string,
	{
		group: ShortcutGroup
		inFields?: true
		label: TKey
		mac: readonly string[]
		onlyUnfocused?: true
		other: readonly string[]
		unlessTextSelected?: true
	}
>

export type ShortcutId = keyof typeof SHORTCUTS

type ShortcutGroup = 'general' | 'listing' | 'selection' | 'create'

export const SHORTCUT_GROUPS = {
	create: 'client:shortcuts.group.create',
	general: 'client:shortcuts.group.general',
	listing: 'client:shortcuts.group.listing',
	selection: 'client:shortcuts.group.selection'
} as const satisfies Record<ShortcutGroup, TKey>

export type Platform = 'mac' | 'other'

const APPLE = /Mac|iPhone|iPad/

// Read when asked, never at import: this module is reachable from code the
// server evaluates, where there is no `navigator`.
export function currentPlatform() {
	return APPLE.test(navigator.userAgent) ? 'mac' : 'other'
}

export interface Step {
	alt: boolean
	ctrl: boolean
	key: string
	meta: boolean
	shift: boolean
}

export function parseSpec(spec: string): Step[] {
	return spec.split(' ').map((step) => {
		const parts = step.split('+')
		const key = parts.at(-1) ?? ''
		const modifiers = new Set(parts.slice(0, -1))

		return {
			alt: modifiers.has('alt'),
			ctrl: modifiers.has('ctrl'),
			key: key === 'space' ? ' ' : key,
			meta: modifiers.has('meta'),
			shift: modifiers.has('shift')
		}
	})
}

export function formatShortcut(spec: string, platform: Platform) {
	const names = MODIFIER_NAMES[platform]

	return parseSpec(spec).map((step) => [
		...(step.ctrl ? [names.ctrl] : []),
		...(step.alt ? [names.alt] : []),
		...(step.shift ? [names.shift] : []),
		...(step.meta ? [names.meta] : []),
		KEY_NAMES[step.key] ?? step.key.toUpperCase()
	])
}

const MODIFIER_NAMES = {
	mac: { alt: '⌥', ctrl: '⌃', meta: '⌘', shift: '⇧' },
	other: { alt: 'Alt', ctrl: 'Ctrl', meta: 'Win', shift: 'Shift' }
} as const satisfies Record<Platform, Record<string, string>>

// Symbols rather than words, so a keycap needs no translation.
const KEY_NAMES: Record<string, string> = {
	' ': '␣',
	arrowdown: '↓',
	arrowleft: '←',
	arrowright: '→',
	arrowup: '↑',
	backspace: '⌫',
	delete: 'Del',
	enter: '↵',
	escape: 'Esc',
	f2: 'F2'
}
